import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

/**
 * Press overview state.
 */
export const usePressOverviewStore = defineStore('press-overview', () => {
  const cacheStore = useCacheStore()
  const pageStore = usePageStore()
  const graphqlFetch = useGraphqlFetch()
  const { locale } = useI18n()

  // Search context
  const page = ref(1)

  // Search results
  const pages = ref<any[]>([]) // our main data source
  const hits = ref<string[]>([])
  const loading = ref(true)
  const totalHits = ref(0)

  // Constants
  const hitsPerPage = computed(() => 12)

  const urlParameters = computed(() => ({
    page: page.value === 1 ? undefined : page.value,
  }))

  // Initialize
  function initialize({ page: pg }: { page?: any }) {
    // When there are no values given, i.e. from the URL, we will simply keep the most recently used search context
    if (pg === undefined) {
      return
    }

    page.value = typeof pg === 'string' ? Math.max(1, Number.parseInt(pg)) : 1
  }

  // Url parameters
  function setUrlParameters(query: any) {
    const toValue = (value: any, fallback: any) => (value === undefined ? fallback : Array.isArray(value) ? value?.[0] : value)
    page.value = Math.max(1, toValue(query.page, 1))
    fetchPresses()
  }
  function reset() {
    page.value = 1
    // note that we do NOT reset the state.tags because this is not a facet that can be set by the user
    fetchPresses()
  }
  // Search context
  function setPage(newPage: number) {
    page.value = Math.max(1, newPage)
    fetchPresses()
  }
  // Search results
  function setHits(newHits: string[]) {
    hits.value = newHits
  }
  function setTotalHits(newTotalHits: number) {
    totalHits.value = newTotalHits
  }
  function setPages(newPages: any[]) {
    pages.value = newPages
  }
  function setLoading(newLoading: boolean) {
    loading.value = newLoading
  }

  async function fetchPresses() {
    const key = ['presses', locale.value].join(';')
    const ttl = 3600

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query searchPress($site: [String]) {
          elasticSearch(site: $site, section: "press", orderBy: "postDate DESC") {
            count
            hits(limit: 1024) {
              uri
            }
          }
        }
        `,
        variables: {
          site: `${locale.value}Default`,
        },
      })

      return { hits: elasticSearch.hits.map(({ uri }: { uri: string }) => uri), totalHits: elasticSearch.count }
    }

    // Fetch the hits
    const { hits: fetchedHits, totalHits: fetchedTotalHits } = await cacheStore.fetch({ key, ttl, fetcher, fallback: { hits: [], totalHits: 0 } })
    setHits(fetchedHits)
    setTotalHits(fetchedTotalHits)

    // todo: add loaders
    const slice = fetchedHits.slice((page.value - 1) * hitsPerPage.value, page.value * hitsPerPage.value)

    // Fetch all associated entries within the current page
    const fetchedPages = await Promise.all(slice.map((uri: string) => pageStore.fetchUri({ uri })))
    setPages(fetchedPages.filter((pageItem) => pageItem))
    setLoading(false)
    return fetchedPages
  }

  return {
    page,
    pages,
    hits,
    loading,
    totalHits,
    hitsPerPage,
    urlParameters,
    initialize,
    setUrlParameters,
    reset,
    setPage,
    setHits,
    setTotalHits,
    setPages,
    setLoading,
    fetch: fetchPresses,
  }
})
